import { cn } from "@/lib/utils";
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import { useMemo } from "react";
import { createTraceTableColumns } from "../TraceTableColumns";
import type { ColumnMeta, EnhancedTrace } from "../types";
import { TraceExpandedContent } from "./TraceExpandedContent";

interface ExpandableTraceTableProps {
  traces: EnhancedTrace[];
}

export function ExpandableTraceTable({ traces }: ExpandableTraceTableProps) {
  // Responsive columns with container queries
  const tableColumns = useMemo(() => {
    return createTraceTableColumns();
  }, []);

  const table = useReactTable({
    data: traces,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowId: (row) => row.traceId, // Use traceId as the row identifier
    enableExpanding: true, // Enable expanding feature
    getRowCanExpand: () => true, // All rows can expand
  });

  const { rows } = table.getRowModel();

  if (traces.length === 0) {
    return (
      <div className="flex items-center justify-center flex-col h-96">
        <div className="text-sm font-medium">No traces found.</div>
        <div className="text-sm text-muted-foreground">
          Note: only the traces of the mcp server that's deployed as part of
          this project are shown here.
        </div>
      </div>
    );
  }

  return (
    <div className="@container flex flex-col h-full space-y-4">
      {/* Responsive Table Layout */}
      <div className="flex-1">
        {/* <div className="rounded-xl border shadow-sm overflow-hidden"> */}
        <div className="overflow-hidden">
          <div className="overflow-y-auto relative">
            <div
              className={cn(
                "grid w-full max-w-full",
                // Responsive grid templates using Tailwind arbitrary values and container queries
                "grid-cols-[40px_60px_1fr_1fr_120px]", // Base: 5 columns
                "@[600px]:grid-cols-[40px_60px_1fr_1fr_80px_120px]", // 6 columns >= 600px
                "@[800px]:grid-cols-[40px_100px_2fr_2fr_1fr_120px]", // updated ratios>= 800px
                "@[1000px]:grid-cols-[40px_100px_3fr_3fr_1fr_120px]", // updated ratios >= 1000px
              )}
            >
              {/* Headers */}
              <div className="contents">
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => {
                    const priority = (
                      header.column.columnDef.meta as ColumnMeta
                    )?.priority;
                    let responsiveClasses = "";

                    // Apply container query classes based on priority
                    if (priority === 2) {
                      responsiveClasses = "@[600px]:block hidden"; // Hide when < 600px
                    } else if (priority === 3) {
                      responsiveClasses = "@[800px]:block hidden"; // Hide when < 800px
                    }

                    return (
                      <div
                        key={header.id}
                        className={cn(
                          "px-3 py-2 text-sm font-medium text-muted-foreground text-left relative",
                          responsiveClasses,
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </div>
                    );
                  }),
                )}
              </div>
              {/* Data rows */}
              {rows.map((row) => (
                <React.Fragment key={row.id}>
                  {/* Row wrapper with contents group for hover effect */}
                  <div className="contents group">
                    {row.getVisibleCells().map((cell) => {
                      const priority = (
                        cell.column.columnDef.meta as ColumnMeta
                      )?.priority;
                      let responsiveClasses = "";

                      // Apply container query classes based on priority
                      if (priority === 2) {
                        responsiveClasses = "@[600px]:block hidden"; // Hide when < 600px
                      } else if (priority === 3) {
                        responsiveClasses = "@[800px]:block hidden"; // Hide when < 800px
                      }

                      return (
                        <div
                          key={cell.id}
                          className={cn(
                            "flex items-center transition-colors px-3 py-2 min-w-0",
                            "cursor-pointer duration-200 hover:bg-muted/50 group-hover:bg-muted/50 border-t",
                            responsiveClasses,
                            (cell.column.columnDef.meta as ColumnMeta)
                              ?.cellClassName,
                          )}
                          onClick={() => {
                            row.toggleExpanded();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              row.toggleExpanded();
                            }
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {row.getIsExpanded() && (
                    <div className="col-span-full border-b p-0 grid">
                      <TraceExpandedContent trace={row.original} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="text-xs text-muted-foreground flex justify-between items-center shrink-0">
        <span>
          Showing {traces.length} trace{traces.length !== 1 ? "s" : ""}
        </span>
        <span className="@[800px]:hidden text-xs text-muted-foreground">
          Compact view - Some columns hidden
        </span>
      </div>
    </div>
  );
}
